
import React, { useEffect, useState } from 'react';
import { registerSW } from 'virtual:pwa-register';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useBeforeUnload } from '@/hooks/use-before-unload';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';

export const UpdateNotification = () => {
  const { t } = useTranslation();
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [updateSWFunction, setUpdateSWFunction] = useState<((reloadPage?: boolean) => Promise<void>) | null>(null);
  
  // Track if there's unsaved data in the application
  const { hasUnsavedChanges, registerUnsavedChangesCheck } = useBeforeUnload();
  
  useEffect(() => {
    try {
      // Register the service worker with a periodic check for updates
      const updateSW = registerSW({
        onNeedRefresh() {
          // When a new version is available, show the mandatory update dialog
          setShowUpdateDialog(true);
          setUpdateSWFunction(() => updateSW);
        },
        onOfflineReady() {
          // No change to offline ready notification - using toast is fine for this
          // as it's not a critical update notification
          toast.success(t('app_ready_offline'), {
            description: t('app_offline_description'),
            duration: 3000,
          });
        },
        // Check for updates every 30 minutes (more frequent for icon updates)
        intervalMS: 30 * 60 * 1000,
        // Force immediate update check
        immediate: true,
      });

      // Force service worker to skip waiting and claim clients immediately
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then((registration) => {
          if (registration.waiting) {
            // Force the waiting service worker to become active
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          }
        });

        // Listen for service worker updates
        navigator.serviceWorker.addEventListener('message', (event) => {
          if (event.data && event.data.type === 'SW_UPDATED') {
            // Force page reload when service worker updates
            window.location.reload();
          }
        });
      }
    } catch (error) {
      console.error('Failed to register service worker:', error);
    }
    
    return () => {
      // Clean up if needed
    };
  }, [t, hasUnsavedChanges]);

  const handleUpdate = () => {
    // Check if there are unsaved changes before updating
    if (hasUnsavedChanges()) {
      setShowUnsavedDialog(true);
    } else if (updateSWFunction) {
      // No unsaved changes, proceed with update
      updateSWFunction(true);
    }
  };

  const handleProceedWithUpdate = () => {
    setShowUnsavedDialog(false);
    if (updateSWFunction) {
      updateSWFunction(true);
    }
  };

  // This component renders both the main update dialog (non-dismissible) 
  // and the unsaved changes dialog when needed
  return (
    <>
      <AlertDialog open={showUpdateDialog} onOpenChange={(open) => {
        // Prevent closing the alert dialog by clicking outside
        // The only way to close it should be the update button
        if (!open && showUpdateDialog) {
          setShowUpdateDialog(true);
        }
      }}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('new_update_available')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('update_description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4">
            <AlertDialogAction onClick={handleUpdate} className="w-full gap-2">
              <RefreshCw className="h-4 w-4" />
              {t('update_now')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('unsaved_changes')}</DialogTitle>
            <DialogDescription>
              {t('unsaved_changes_update_warning')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 flex justify-between sm:justify-between">
            <Button variant="outline" onClick={() => setShowUnsavedDialog(false)}>
              {t('cancel')}
            </Button>
            <Button variant="destructive" onClick={handleProceedWithUpdate}>
              {t('discard_and_update')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
